import { Component, Inject, InjectionToken, OnDestroy, ChangeDetectionStrategy, SecurityContext } from '@angular/core';
import { MdlDialogReference } from '@angular-mdl/core';
import { HttpClient, HttpResponse, HttpEventType, HttpEvent, HttpProgressEvent, HttpErrorResponse, } from '@angular/common/http';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DlLink } from '../../documents/models/dlLink';
import {EQueryAttachmentDownloadLink} from '../../e-queries/models/e-query-attachment-download-link';
import { SampleReportInfo } from '../../documents/models/sample-report-info';
import { DomSanitizer } from '@angular/platform-browser';

export const DL_LINKS_TOKEN = new InjectionToken('dl-links');

export interface DlTask {
    params: DlLink | DlLink[] | EQueryAttachmentDownloadLink;
    url: string;
}

const enum DownloadState {
    Start,
    InProgress,
    NotFoundError,
    UnknownError,
    SuccessfullyCompleted
}

interface DownloadProgress {
    loaded: number;
}

@Component({
    selector: 'download-dialog',
    styles: [require('./download-dialog.component.scss')],
    template: require('./download-dialog.component.html'),
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadDialogComponent implements OnDestroy {
    private dlSubscription: Subscription;

    private readonly downloadState$ = new BehaviorSubject<DownloadState>(DownloadState.Start);
    private readonly downloadProgress$ = new BehaviorSubject<DownloadProgress>(null);

    private readonly shouldOpenInNewTab: boolean = false;

    public get isStateStart(): Observable<boolean> {
        return this.downloadState$.pipe(map(state => state === DownloadState.Start));
    }

    public get isStateInProgress(): Observable<boolean> {
        return this.downloadState$.pipe(map(state => state === DownloadState.InProgress));
    }

    public get isStateNotFoundError(): Observable<boolean> {
        return this.downloadState$.pipe(map(state => state === DownloadState.NotFoundError));
    }

    public get isStateUnknownError(): Observable<boolean> {
        return this.downloadState$.pipe(map(state => state === DownloadState.UnknownError));
    }

    public get downloadProgress(): Observable<DownloadProgress> {
        return this.downloadProgress$;
    }

    public get closeButtonLabel(): Observable<string> {
        return this.downloadState$.pipe(
            map(state =>
                state === DownloadState.NotFoundError || state === DownloadState.UnknownError
                ? 'Close'
                : 'Cancel')
        );
    }

    constructor(
        private http: HttpClient,
        private dialog: MdlDialogReference,
        private sanitizer: DomSanitizer,
        @Inject(DL_LINKS_TOKEN) dlTask: DlTask
    ) {
        if (dlTask.params instanceof EQueryAttachmentDownloadLink && dlTask.params.shouldOpenInNewTab) {
            this.shouldOpenInNewTab = dlTask.params.shouldOpenInNewTab;
        }
        this.startDownloading(dlTask.url, dlTask.params);
    }

    public close() {
        this.dialog.hide(false);
    }

    public ngOnDestroy() {
        this.dlSubscription.unsubscribe();
        this.downloadState$.complete();
        this.downloadProgress$.complete();
    }

    private startDownloading(url: string, data: DlLink | DlLink[] | EQueryAttachmentDownloadLink | SampleReportInfo) {
        this.dlSubscription = this.http
            .post(url, data, { observe: 'events', responseType: 'blob', reportProgress: true })
            .subscribe({
                next: this.handleDownloadHttpEvent,
                error: this.handleDownloadError
            });
    }

    private handleDownloadHttpEvent = (event: HttpEvent<Blob>) => {
        switch (event.type) {
            case HttpEventType.DownloadProgress:
                this.handleDownloadProgress(event);
                break;
            case HttpEventType.Response:
                this.handleOkResponse(event);
                break;
        }
    }

    private handleOkResponse(response: HttpResponse<Blob>) {
        if (!response.ok) {
            this.downloadState$.next(DownloadState.NotFoundError);
            return;
        }

        const link = document.createElement('a') as HTMLAnchorElement;

        const filename = this.getFileName(response);
        const contentType = this.getContentType(response);
        const myBlob = new Blob([response.body], { type: contentType });

        if (!myBlob.size) {
            this.downloadState$.next(DownloadState.NotFoundError);
            return;
        }

        if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(myBlob, filename);
        } else {
            const fileURL = URL.createObjectURL(myBlob);
            link.href = this.sanitizer.sanitize(SecurityContext.NONE, fileURL);

            if (this.shouldOpenInNewTab) {
                link.target = '_blank';
            } else {
                link.download = filename;
            }

            //Firefox requires the link to be added to the document
            document.body.appendChild(link);
            link.click();
            URL.revokeObjectURL(fileURL);
            document.body.removeChild(link);
        }

        this.downloadState$.next(DownloadState.SuccessfullyCompleted);
        this.dialog.hide(true);
    }

    private handleDownloadProgress(event: HttpProgressEvent) {
        this.downloadProgress$.next({ loaded: event.loaded });
        this.downloadState$.next(DownloadState.InProgress);
    }

    private handleDownloadError = (error: HttpErrorResponse) => {
        if (error.status === 404) {
            this.downloadState$.next(DownloadState.NotFoundError);
            return;
        }

        this.downloadState$.next(DownloadState.UnknownError);
    }

    private getFileName(response: HttpResponse<any>) {
        const defaultFileName = 'unknown';
        const filenameHeader = response.headers.get('content-disposition');
        if (!filenameHeader) {
            return defaultFileName;
        }
        const fileName = filenameHeader.split('filename=')[1] || defaultFileName;
        return decodeURIComponent(fileName).replace(/"/g, '');
    }

    private getContentType(response: HttpResponse<any>) {
        return response.headers.get('content-type') || 'application/octet-stream';
    }
}